<script setup lang="ts">
import { onMounted, ref, shallowRef } from 'vue';
import { cloneDeep } from 'lodash';
import InvestorProfileDialog from '@/modules/investor-profiles/components/InvestorProfileDialog.vue';
import { InvestorProfile, RestOperation } from '@/utils/helpers/rest';
import { errorString, i18nServerMessage } from '@/utils/helpers/rest-response';
import i18n from '@/localisation/i18n';
import { DataTableHeader } from 'vuetify';
import axios, { AxiosError } from 'axios';
import { ApiError } from '@/utils/errors/api-error';
import { useDialogManager } from '@/plugins/dialog-manager';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import logger from '@/modules/common/services/logger.service';

const dialog = useDialogManager();
const snackbar = useSnackbarManager();

const defaultProfile = {
  id: '',
  label: '',
};

const tableColumns: DataTableHeader[] = [
  { text: '', value: 'icon', sortable: false, width: 16, class: 'row-icon', cellClass: 'row-icon' },
  { text: i18n.tc('investorProfile'), value: 'label', class: 'text-no-wrap text-truncate' },
  { text: '', value: 'actions', sortable: false },
];

const investorProfiles = shallowRef<InvestorProfile[]>([]);
const tableSearch = ref('');

const addBuffer = shallowRef<Partial<NullableAll<InvestorProfile>> | null>(null);
const dialogBuffer = shallowRef<Partial<NullableAll<InvestorProfile>> | null>(null);
const dialogOptions = ref({
  isActive: false,
  crudAction: RestOperation.Noop,
  title: 'Profile',
  cancelOptions: ['escape', 'outside'], // no 'X' for real FORMs
});

async function fetchAdminInvestorProfiles(): Promise<void> {
  try {
    const { data } = await axios.get<{ profiles: InvestorProfile[] }>(
      '/api/1/broker-admin/investor-profiles'
    );
    investorProfiles.value = data.profiles;
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

async function deleteInvestorProfile(profile: InvestorProfile): Promise<void> {
  try {
    await axios.delete(`/api/1/broker-admin/investor-profiles/${profile.id}`);
    await fetchAdminInvestorProfiles();
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

function createProfile(): void {
  dialogOptions.value.isActive = true;
  dialogOptions.value.crudAction = RestOperation.Create;
  dialogOptions.value.title = 'Add Profile';

  // addBuffer is a 2nd buffer behind the dialog so that the admin
  // can escape from `Create New Profile` and return with partially filled record
  // browsing the profile list will not destroy the partially filled record
  if (addBuffer.value == null) {
    addBuffer.value = cloneDeep(defaultProfile);
  }
  dialogBuffer.value = addBuffer.value;
}

function editProfile(rowProfile: InvestorProfile): void {
  dialogOptions.value.isActive = true;
  dialogOptions.value.crudAction = RestOperation.Update;
  dialogOptions.value.title = 'Change Profile';
  // do not modify the row while typing
  dialogBuffer.value = cloneDeep(rowProfile);
}

async function closeModal(): Promise<void> {
  // wipe user data
  dialogOptions.value.isActive = false;
  dialogOptions.value.crudAction = RestOperation.Noop;
  dialogOptions.value.title = '';
  addBuffer.value = null;
  dialogBuffer.value = null;

  // refresh the list after create/update
  try {
    await fetchAdminInvestorProfiles();
  } catch (e) {
    logger.warn(e);
  }
}

function deleteProfile(rowProfile: InvestorProfile): void {
  dialog.ask({
    title: i18n.t('deleteInvestorProfile'),
    color: 'error',
    icon: 'mdi-alert',
    message: i18n.t('deleteInvestorProfileMessage', { profileLabel: rowProfile.label }),
    acceptText: i18n.t('deleteInvestorProfile'),
    onAccept: () => doDeleteProfile(rowProfile),
    rejectText: i18n.t('cancelButton'),
  });
}

async function doDeleteProfile(rowProfile: InvestorProfile) {
  try {
    await deleteInvestorProfile(rowProfile);

    snackbar.confirm(i18n.tc('deletedInvestorProfile'));
  } catch (e) {
    snackbar.error(errorString(e as Error));
  }
}

onMounted(async () => {
  try {
    await fetchAdminInvestorProfiles();
  } catch (e) {
    logger.warn(e);
  }
});
</script>

<template>
  <div>
    <!-- add company account -->
    <VBtn class="ma-6" color="primary" @click="createProfile">
      <VIcon left> mdi-plus</VIcon>
      {{ $t('addInvestorProfile') }}
    </VBtn>

    <!-- search bar -->
    <VTextField
      v-model="tableSearch"
      class="mx-6"
      clearable
      :label="$t('searchInvestorProfile')"
      prepend-inner-icon="mdi-magnify"
    ></VTextField>

    <VDataTable
      class="elevation-0 pb-8"
      dense
      disable-pagination
      fixed-header
      :headers="tableColumns"
      hide-default-footer
      :items="investorProfiles"
      :no-data-text="
        $tc('no profiles', investorProfiles.length, { count: investorProfiles.length })
      "
      :search="tableSearch"
    >
      <!-- profile icon -->
      <!-- eslint-disable-next-line vue/valid-v-slot -->
      <template #item.icon>
        <VIcon disabled small> mdi-currency-usd</VIcon>
      </template>

      <!-- action buttons -->
      <!-- eslint-disable-next-line vue/valid-v-slot -->
      <template #item.actions="{ item }">
        <VTooltip color="primary" top>
          <template #activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-btn class="ma-1" color="primary" icon x-small @click="editProfile(item)">
                <VIcon> mdi-database-edit </VIcon>
              </v-btn>
            </span>
          </template>
          <span>{{ $t('editInvestorProfile') }}</span>
        </VTooltip>

        <VTooltip color="primary" top>
          <template #activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <VBtn class="ma-1" color="primary" icon x-small @click="deleteProfile(item)">
                <VIcon> mdi-database-remove </VIcon>
              </VBtn>
            </span>
          </template>
          <span>{{ $t('deleteInvestorProfile') }}</span>
        </VTooltip>
      </template>
    </VDataTable>

    <InvestorProfileDialog
      v-if="dialogOptions.isActive && dialogBuffer"
      :crud-action="dialogOptions.crudAction"
      :profile="dialogBuffer"
      :title="dialogOptions.title"
      @close-modal="closeModal"
    ></InvestorProfileDialog>
  </div>
</template>

<style lang="scss" scoped></style>
